import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

/**
 * Global banner shown while an admin is impersonating another user
 * (Task #1351). Rendered above the app content; the "back to my account"
 * button restores the original admin session without re-login.
 */
export default function ImpersonationBanner() {
  const { t } = useTranslation();
  const { user, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [stopping, setStopping] = useState(false);

  if (!user?.impersonator) return null;

  async function handleStop() {
    setStopping(true);
    try {
      await stopImpersonation();
      navigate("/overview", { replace: true });
    } finally {
      setStopping(false);
    }
  }

  return (
    <div
      role="status"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem",
        flexWrap: "wrap",
        padding: "0.5rem 1rem",
        background: "#7c3aed",
        color: "#fff",
        fontSize: "0.9rem",
        fontWeight: 600,
        boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
      }}
    >
      <span>
        👁 {t("impersonation.banner", { name: user.full_name || user.username })}
      </span>
      <button
        onClick={handleStop}
        disabled={stopping}
        style={{
          background: "#fff",
          color: "#7c3aed",
          border: "none",
          borderRadius: 5,
          padding: "0.3rem 0.85rem",
          fontWeight: 700,
          fontSize: "0.85rem",
          cursor: stopping ? "wait" : "pointer",
        }}
      >
        {stopping ? "…" : t("impersonation.stop")}
      </button>
    </div>
  );
}
